import {useCallback, useState} from 'react'

import type {CodeLineData} from '../components/blob/BlobContent/Code/hooks/use-code-lines'

export const DELETE_STICKY_LINES_VALUE = null

export type SetStickyLinesType = (codeLineData: CodeLineData | null, removeStickyLine: boolean) => void

export function useStickyLines() {
  const [currentStickyLines, setCurrentStickyLines] = useState<Map<number, CodeLineData>>(() => new Map())

  const setStickyLines = useCallback<SetStickyLinesType>((codeLineData, removeStickyLine) => {
    setCurrentStickyLines(prev => {
      // No-op delete when already empty
      if (codeLineData === null && prev.size === 0) {
        return prev
      }

      // Clear all sticky lines
      if (codeLineData === null && removeStickyLine) {
        if (prev.size === 0) {
          return prev
        }
        return new Map()
      }

      if (!codeLineData) {
        return prev
      }

      const lineNumber = codeLineData.lineNumber
      const next = new Map(prev)

      if (removeStickyLine) {
        next.delete(lineNumber)
      } else {
        next.set(lineNumber, codeLineData)
      }

      const activeLine = next.get(lineNumber)
      if (activeLine) {
        for (const [otherLineNumber, otherStickyLine] of next) {
          // Skip the line we just added/updated
          if (otherLineNumber === lineNumber) {
            continue
          }

          // Delete lines that are outside valid scope:
          // - No ownedSection, OR
          // - Section ends before current line, OR
          // - Current line is before the other line
          if (
            !otherStickyLine.ownedSection ||
            otherStickyLine.ownedSection.endLine < lineNumber ||
            lineNumber < otherStickyLine.lineNumber
          ) {
            next.delete(otherLineNumber)
          }
        }
      }

      return next
    })
  }, [])

  return {currentStickyLines, setStickyLines}
}
